/**
 * ScrollToTop — resets the viewport scroll position to (0, 0) every time
 * the route changes. React Router v6 by default preserves scroll across
 * navigations (mimicking SPA back-button behaviour), which on a content-
 * heavy app like LexIQ feels broken: scrolling halfway down /cases and
 * tapping the Account tab opens /account already scrolled halfway down too.
 *
 * Mount this once, directly inside `<BrowserRouter>` and BEFORE the
 * `<Routes>` block.
 *
 * Implementation notes:
 *   • We scroll BOTH `window` and the documentElement / body to cover
 *     browsers that disagree on which one is scrollable.
 *   • We also reset every fixed-positioned scrollable region opted-in via
 *     `data-scroll-reset` so authenticated-area sub-scrollers (e.g. an
 *     inner `<main>` with its own `overflow-y-auto`) also jump back to top.
 *   • `behavior: "instant"` to avoid a jarring smooth-scroll mid-transition.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

export default function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    } catch {
      // Older Safari throws on `behavior:"instant"` — fall back to legacy API.
      window.scrollTo(0, 0);
    }
    if (document.documentElement) document.documentElement.scrollTop = 0;
    if (document.body) document.body.scrollTop = 0;

    // Opt-in scrollable regions inside the page can declare themselves with
    // `data-scroll-reset` so they also reset on every route change.
    document.querySelectorAll("[data-scroll-reset]").forEach((el) => {
      el.scrollTop = 0;
    });
  }, [pathname]);

  return null;
}
